import * as React from 'react';
import JRCFormFieldRender, { InputState, JRCWritableFormInputProps } from '../JRCFormFieldRender';
import { useControls } from '../ReduxFormControls';
import { useIntl } from 'react-intl';
import { Field, WrappedFieldInputProps } from 'redux-form';
import * as Components from './JRCFormRichEditorField.styles';

import { JRCHtml } from 'components/JRCHtml/JRCHtml';
import { DataCy } from '../../../../../types/dataAttributes';
import { JRCInputTinyMCERaw, MentionQuery } from '../../JRCInputTinyMCERaw/JRCInputTinyMCERaw';
import { TinyMCECommonOptions } from 'jamespot-user-api';
import { useEffect, useState } from 'react';

export type FormRichTextFieldOptionsProps = {
    commonOptions: TinyMCECommonOptions;
    mentionsQueries: {
        users: MentionQuery;
        tags: MentionQuery;
        contents: MentionQuery;
    };
};

export type RichTextValue = {
    html: string;
    text: string;
};

export type FormRichTextFieldProps = {
    name: string;
    placeholder?: string;
    value: Partial<RichTextValue> | undefined;
    onChange: (value: RichTextValue) => void;
    readOnly?: boolean;
} & DataCy;

/**
 * This component is used with redux-form (studio) and with react-hook-form!!
 */
// eslint-disable-next-line react/display-name
export const FormRichTextField = React.forwardRef(
    (
        props: FormRichTextFieldProps & { inplace?: boolean; isComponentVisible?: boolean } & {
            options: FormRichTextFieldOptionsProps;
        },
        ref: any,
    ) => {
        const [displayedValue, setDisplayedValue] = useState(props.value);

        const { onChange, value } = props;
        useEffect(() => {
            const timeout = setTimeout(() => {
                if (displayedValue?.html !== value?.html) {
                    onChange({ html: displayedValue?.html ?? '', text: displayedValue?.text ?? '' });
                }
            }, 500);

            return () => clearTimeout(timeout);
        }, [displayedValue, onChange, value]);

        return props.readOnly ? (
            <Components.ReadOnlyBorder>
                <JRCHtml __html={displayedValue?.html ?? ''} />
            </Components.ReadOnlyBorder>
        ) : (
            <JRCInputTinyMCERaw
                ref={ref}
                value={displayedValue?.html ?? ''}
                onChange={(html, text) => setDisplayedValue({ html, text })}
                mentionsQueries={props.options.mentionsQueries}
                commonOptions={props.options.commonOptions}
            />
        );
    },
);

/**
 * Component used as a <input type="text"/>
 * @param props JRCWritableFormInputProps
 * validation props: required
 * @returns JSX.Element
 */
export const JRCFormRichTextField = (props: JRCWritableFormInputProps & { tinyMCE: FormRichTextFieldOptionsProps }) => {
    const validate = useControls({
        intl: useIntl(),
        required: props.required,
    });
    const { tinyMCE: tinyMCEProps, ...formProps } = props;
    return (
        <Field
            component={JRCFormFieldRender}
            renderInputFct={(input: WrappedFieldInputProps, inputState: InputState) => (
                <FormRichTextField {...props} {...input} {...inputState} dataCy={props.dataCy} options={tinyMCEProps} />
            )}
            validate={validate}
            htmlFor={props.name}
            {...formProps}
        />
    );
};
